import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { timeEntriesAPI, projectsAPI } from '@/services/api';
import { toast } from 'sonner';

const STORAGE_KEY = 'rektra_reminder_settings';
const SHOWN_KEY = 'rektra_reminder_last_shown';
const SUGGEST_KEY = 'rektra_reminder_suggest';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_SUGGESTIONS = 2;

export function getReminder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { enabled: false, time: '17:00' };
  } catch {
    return { enabled: false, time: '17:00' };
  }
}

export function setReminder(settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

function alreadyShownToday() {
  const last = localStorage.getItem(SHOWN_KEY);
  if (!last) return false;
  return last === new Date().toISOString().slice(0, 10);
}

function markShownToday() {
  localStorage.setItem(SHOWN_KEY, new Date().toISOString().slice(0, 10));
}

function isPastTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const now = new Date();
  return now.getHours() > h || (now.getHours() === h && now.getMinutes() >= m);
}

export function useReminders() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    if (!user) return;

    const settings = getReminder();
    if (!settings.enabled) return;
    if (!isPastTime(settings.time)) return;
    if (alreadyShownToday()) return;

    // Check if user has logged hours today
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await timeEntriesAPI.list({ date: today });
      const entries = res?.data || [];
      if (entries.length > 0) {
        // Hours already logged — no reminder needed
        markShownToday();
        return;
      }
    } catch {
      // If API fails, skip this check
      return;
    }

    markShownToday();

    const title = lang === 'is' ? 'Rektra' : 'Rektra';
    const body = lang === 'is'
      ? 'Ekki gleyma að skrá tímana þína í dag!'
      : "Don't forget to log your hours today!";

    // Try browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, { body, icon: '/logos/favicon-black.png' });
      } catch {
        // Fallback to toast
      }
    }

    // Always show in-app toast as well
    toast(body, { duration: 8000 });
  }, [user, lang]);

  useEffect(() => {
    // Initial check after 10 seconds (let app settle)
    const timeout = setTimeout(check, 10000);
    // Periodic checks
    intervalRef.current = setInterval(check, CHECK_INTERVAL);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [check]);
}

// ── Smart suggestion: show prompt if no hours for 2 days ──
function getSuggestState() {
  try {
    const raw = localStorage.getItem(SUGGEST_KEY);
    return raw ? JSON.parse(raw) : { count: 0, lastDismissed: null };
  } catch {
    return { count: 0, lastDismissed: null };
  }
}

function setSuggestState(state) {
  localStorage.setItem(SUGGEST_KEY, JSON.stringify(state));
}

export function useReminderSuggestion() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!user || checkedRef.current) return;
    checkedRef.current = true;

    const settings = getReminder();
    if (settings.enabled) return; // Already enabled, no need to suggest

    const suggestState = getSuggestState();
    if (suggestState.count >= MAX_SUGGESTIONS) return; // Already shown max times

    // Don't show again within 7 days of last dismiss
    if (suggestState.lastDismissed) {
      const daysSince = (Date.now() - new Date(suggestState.lastDismissed).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // Never on the first login, and never in the first 24h (iter324): a
    // brand-new owner has nothing to be reminded about and the popup landed
    // on top of the trial bar. `created_at` is the source of truth; the
    // locally stamped first-seen time is the fallback when it is absent.
    const FIRST_SEEN_KEY = `rektra.firstSeen.${user.user_id}`;
    let firstSeen = null;
    try {
      firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
      if (!firstSeen) {
        localStorage.setItem(FIRST_SEEN_KEY, new Date().toISOString());
        return; // first login, by definition
      }
    } catch { /* storage unavailable, fall through to created_at */ }

    const stamp = user.created_at || firstSeen;
    if (!stamp) return;
    const ageHours = (Date.now() - new Date(stamp).getTime()) / (1000 * 60 * 60);
    if (!(ageHours >= 24)) return;

    (async () => {
      try {
        // Needs at least one project — nothing to log hours against otherwise.
        const projects = await projectsAPI.list();
        if (!(projects?.data || []).length) return;

        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const dayBefore = new Date(today);
        dayBefore.setDate(dayBefore.getDate() - 2);

        const fmt = (d) => d.toISOString().slice(0, 10);

        const [r1, r2] = await Promise.all([
          timeEntriesAPI.list({ date: fmt(yesterday) }),
          timeEntriesAPI.list({ date: fmt(dayBefore) }),
        ]);

        const has1 = (r1?.data || []).length > 0;
        const has2 = (r2?.data || []).length > 0;

        if (!has1 && !has2) {
          // No hours for 2 consecutive days → suggest
          setShow(true);
        }
      } catch {
        // API error, skip
      }
    })();
  }, [user]);

  const dismiss = useCallback(() => {
    setShow(false);
    const s = getSuggestState();
    setSuggestState({ count: s.count + 1, lastDismissed: new Date().toISOString() });
  }, []);

  const enable = useCallback(() => {
    setShow(false);
    const updated = { enabled: true, time: '17:00' };
    setReminder(updated);
    const s = getSuggestState();
    setSuggestState({ count: MAX_SUGGESTIONS, lastDismissed: new Date().toISOString() }); // Don't show again

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return { show, dismiss, enable };
}
